/*
 * Copyright © 2021-present Tacnode Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Tacnode Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Tacnode Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { Col, Row, Table, TableColumnsType } from 'antd'
import { useRef, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ModelProductAndAmount } from 'account-client'
import * as echarts from 'echarts/core'
import { useTranslateAmount } from '@/hooks/useTranslateAmount'
import { useTranslateEnum } from '@/hooks/useTranslateEnum'
import { LayoutOthers } from '@/layouts/LayoutOthers'
import { ReactEcharts } from '@/libs/ReactEcharts'
import { RouteSupplyData } from '@/routes/RouteSupplyData'

export function ProductSummary() {
  const { t } = useTranslation()
  const { translate } = useTranslateEnum()
  const { currency, translateAmount } = useTranslateAmount()
  const { data: statementDetail } = RouteSupplyData.useContext('statementId')
  const eChartsInstanceRef = useRef<echarts.ECharts>()

  const columns = useMemo<TableColumnsType<ModelProductAndAmount>>(
    () => [
      {
        title: t('Product'),
        dataIndex: 'product',
        render: value => translate(value),
      },
      {
        title: `${t('Total Amount')}（${currency}）`,
        dataIndex: 'amountFormatted',
        // defaultSortOrder: 'descend',
        sorter: (a, b) => Number(a.amount) - Number(b.amount),
      },
    ],
    [],
  )

  useEffect(() => {
    eChartsInstanceRef.current?.setOption({
      series: [
        {
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['80%', '60%'],
          itemStyle: {
            borderRadius: 3,
          },
          label: false,
          data: statementDetail.statementData.productAndAmounts
            ?.sort((a, b) => Number(b.amount) - Number(a.amount))
            .map(item => ({
              name: `${translate(item.product)}: ${translateAmount(item.amountFormatted)}`,
              value: item.amount,
            })),
        },
      ],
    })
  }, [statementDetail])

  return (
    <Row gutter={24}>
      <Col span={10}>
        <LayoutOthers.ChartBox>
          <ReactEcharts
            loading={false}
            hideZoomButton={true}
            style={{ height: 200 }}
            baseOption={{
              tooltip: {
                formatter: '{b}（{d}%）',
                // valueFormatter: (value: any) => `${Intl.NumberFormat().format(value)} 元`,
              },
              legend: {
                top: '25',
                left: '0',
                orient: 'vertical',
              },
            }}
            onChartReady={instance => {
              eChartsInstanceRef.current = instance
            }}
          />
        </LayoutOthers.ChartBox>
      </Col>
      <Col span={14}>
        <Table
          columns={columns}
          dataSource={statementDetail.statementData.productAndAmounts}
          rowKey={record => record.product}
          pagination={false}
          scroll={{ y: 220 }}
          size="small"
        />
      </Col>
    </Row>
  )
}
